import React, { FunctionComponent, ReactNode } from 'react'
import { Avatar, Button, Text, useIsDesignLanguage, DesignLanguage } from '@afterpaytouch/core'
import { useTranslation } from 'next-i18next'
import { useCheckoutSizes } from '../../utils/responsive'
import styles from './style.module.css'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import {
  convertConsumerDateOfBirth,
  useAppDispatch,
  useAppRouter,
  useConsumerContactAddress,
  useConsumerName,
  useConsumerSelector,
  useCountry,
} from '../../state'
import { Route } from '../../config/router'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { Features, isCountryHardIdvSkipDocumentSelection } from '../../model'
import { InfoModal, InfoModalProps } from './infoModal'
import { isUndefinedOrEmpty } from '@afterpay/utils'
import clsx from 'clsx'
import { useFlag } from '@afterpaytouch/feature'
import { Country } from '@afterpay/types'
import { setBiometricOptOut } from '../../state/identity/reducer'

export const VerifyStart: FunctionComponent = () => {
  const { t } = useTranslation()
  const { textSize, smallTextSize, sectionHeadingSize } = useCheckoutSizes()
  const { profile, email } = useConsumerSelector()
  const address = useConsumerContactAddress()
  const dateOfBirth = profile?.dateOfBirth
  const consumerName = useConsumerName()
  const router = useAppRouter()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const country = useCountry()
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const isConsumerAddressInQuebec = address?.countryCode === Country.CA && address?.state === 'QC'
  const caIdvQuebecEnabled = useFlag<boolean>(Features.CA_IDV_QUEBEC_ENABLED, false)
  const isCaIdvQuebecEnabled = isConsumerAddressInQuebec && caIdvQuebecEnabled

  const dispatch = useAppDispatch()
  const hasTranslation = (key: string): boolean => {
    const translation = t(key, { defaultValue: '' })
    return Boolean(translation && translation !== key && !isUndefinedOrEmpty(translation))
  }

  const handleStart = (biometricOptOut: boolean): void => {
    dispatch(setBiometricOptOut(biometricOptOut))
    logEvent(TrackingEvent.PRESSED_UPLOAD_MY_ID_BUTTON_ON_IDENTITY_START_PAGE)
    const skipDocumentSelection = isCountryHardIdvSkipDocumentSelection(country)
    if (skipDocumentSelection) {
      router.push(Route.VERIFY_BIOMETRIC_PERSONA)
    } else {
      router.push(Route.VERIFY_IDENTITY)
    }
  }

  const renderDocumentList = (): ReactNode => {
    const documentListOptions: Array<{ content: string; info?: InfoModalProps }> = t('verifyIdentity:start:documents', {
      returnObjects: true,
      defaultValue: [],
    })
    if (!Array.isArray(documentListOptions) || documentListOptions.length === 0) {
      return null
    }

    return (
      <ul className={styles.documentList}>
        {documentListOptions.map(
          (option, i) =>
            option !== null &&
            'content' in option && (
              <li key={`verify-start-document-${i}`} className={clsx(styles.documentItem, option.info != null && styles.documentItemWithInfo)}>
                <div className={styles.documentItemContent}>
                  <Text size={textSize}>{option.content}</Text>
                  {option.info != null && <InfoModal {...option.info} />}
                </div>
              </li>
            )
        )}
      </ul>
    )
  }

  return (
    <div className={clsx(styles.container, isBronteDesignLanguage && styles.withBorder)}>
      <MaskedElement actionName='Verify Info'>
        <div className={styles.profileCard}>
          <Avatar size='M' src='' alt={consumerName} name={consumerName} title={consumerName} />
          <div className='info'>
            <Text bold size={sectionHeadingSize}>
              {consumerName}
            </Text>
            <Text size={smallTextSize} color='Gray50'>
              {convertConsumerDateOfBirth({ dateOfBirth, locale: router.locale })}
            </Text>
            <Text size={smallTextSize} color='Gray50'>
              {email}
            </Text>
          </div>
        </div>
      </MaskedElement>
      <div className={styles.description}>
        <Text size={textSize}>{t('verifyIdentity:start:description')}</Text>
      </div>
      <Text size={textSize}>{t('verifyIdentity:start:detail')}</Text>
      {renderDocumentList()}
      {hasTranslation('verifyIdentity:start:reminder') && (
        <div className={styles.reminder}>
          <Text size={smallTextSize} color='Gray50'>
            {t('verifyIdentity:start:reminder')}
          </Text>
        </div>
      )}
      {isCaIdvQuebecEnabled && (
        <div className={styles.quebec}>
          <Text size={smallTextSize} color='Gray50'>
            {t('verifyIdentity:start:quebecDescription')}
          </Text>
          <Button.Ghost testNameSpace='verify-verify-start-upload-quebec-id-only' padding='Fluid' onClick={() => handleStart(true)} size='Regular'>
            {t('verifyIdentity:start:ctaQuebecIdOnly')}
          </Button.Ghost>
        </div>
      )}
      <Button.Primary testNameSpace='verify-verify-start-upload' padding='Fluid' onClick={() => handleStart(false)} size='Regular'>
        {isCaIdvQuebecEnabled ? t('verifyIdentity:start:ctaQuebecWithSelfie') : t('verifyIdentity:start:cta')}
      </Button.Primary>
    </div>
  )
}
